<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

import logo from '@/app/assets/logo_color.svg'
import { routeCatalogues, useI18n } from '@/app/i18n'
import { identifyCrispUser } from '@/app/integrations/crisp'
import { loadAccount, refreshSession } from '@/app/session'
import { useAuthStore } from '@/app/stores/auth'

import UserMenu from './components/UserMenu.vue'

/**
 * The onboarding frame: the organisation setup wizard of a workspace that is not set up yet (`meta.onboarding` routes
 * while `isWorkspaceSetUp()` is false — App.vue). A slim bar with the logo, "Setting up <workspace>" and the user menu
 * (profile, logout); no nav, breadcrumb, navbar actions, suggestion box or footer: every other page redirects here
 * anyway (the wizard gate, wp/shell §5.2), so the frame shows only what the wizard needs. The nav's own rule
 * (`VerticalNav`: no menu until `WizzardCompleted`) is unchanged for the other pages. `data-app-content` marks the
 * content the e2e captures read (tests/e2e/helpers/content.ts).
 */
const { t } = useI18n()
const route = useRoute()
const auth = useAuthStore()
const workspaceName = computed(() => String(auth.currentWorkspace?.Label ?? ''))

/**
 * What the vertical layout loads on mount that the wizard needs too: the account (its language becomes the UI language,
 * its date format) and, for Staff, the permissions and softwares (the app tiles). Not the workspace list or the badge
 * polling: nothing here shows them.
 */
onMounted(() => {
  if (!auth.session) return
  if (auth.userType === 'Staff') void refreshSession().catch(() => undefined)
  void loadAccount(routeCatalogues(route.meta))
    .then(() => {
      if (auth.session) identifyCrispUser(auth.session.profile, auth.session.currentWorkspace)
    })
    .catch(() => undefined)
})
</script>

<template>
  <div class="layout-onboarding flex min-h-screen flex-col" data-layout="onboarding">
    <header class="layout-onboarding__bar" data-testid="onboarding-bar">
      <div class="flex min-w-0 items-center gap-5">
        <RouterLink to="/" class="shrink-0">
          <img :src="logo" alt="Skileo" width="110" />
        </RouterLink>
        <span v-if="workspaceName" class="hidden h-6 w-px bg-surface-200 sm:block" aria-hidden="true" />
        <span v-if="workspaceName" class="hidden truncate sm:inline" data-testid="onboarding-workspace">
          {{ t('shell.wizard.hint.settingUp', { name: workspaceName }) }}
        </span>
      </div>
      <UserMenu />
    </header>
    <main class="layout-onboarding__content" data-app-content>
      <slot />
    </main>
  </div>
</template>

<style scoped>
.layout-onboarding__bar {
  position: sticky;
  top: 0;
  z-index: 12;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  height: 4.5rem;
  padding: 0 2rem;
  background: var(--p-surface-0);
  border-bottom: 1px solid var(--p-content-border-color);
}
.layout-onboarding__content {
  flex: 1;
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 3rem;
}
@media (max-width: 575px) {
  .layout-onboarding__bar {
    padding: 0 1rem;
  }
  .layout-onboarding__content {
    padding: 1.5rem 1rem 2.5rem;
  }
}
</style>
